import type { ConversionSettings, TargetMimeType } from "../types";
import { convertAudioWithFfmpeg } from "./ffmpegClient";

export const MAX_AUDIO_BYTES = 500 * 1024 * 1024;

export const MIME_TO_EXT: Record<TargetMimeType, string> = {
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/mp4": "m4a",
  "audio/flac": "flac",
  "audio/ogg": "ogg",
};

export const ACCEPTED_MIME = [
  "audio/aac",
  "audio/flac",
  "audio/m4a",
  "audio/mp4",
  "audio/mpeg",
  "audio/ogg",
  "audio/opus",
  "audio/wav",
  "audio/wave",
  "audio/webm",
  "audio/x-aac",
  "audio/x-flac",
  "audio/x-m4a",
  "audio/x-wav",
];

export const ACCEPTED_EXT = [
  ".wav",
  ".mp3",
  ".m4a",
  ".aac",
  ".flac",
  ".ogg",
  ".opus",
  ".webm",
];

export const getFileExtension = (filename: string): string => {
  const ext = filename.split(".").pop();
  return ext ? ext.toLowerCase() : "";
};

export const getExtensionFromMime = (mimeType: TargetMimeType): string =>
  MIME_TO_EXT[mimeType] ?? "mp3";

export const getOutputName = (
  filename: string,
  targetFormat: TargetMimeType,
): string => {
  const dotIndex = filename.lastIndexOf(".");
  const baseName = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  return `${baseName}.${getExtensionFromMime(targetFormat)}`;
};

export const isOversizedAudioFile = (file: File): boolean =>
  file.size > MAX_AUDIO_BYTES;

export const isSupportedAudioFile = (file: File): boolean => {
  if (isOversizedAudioFile(file)) return false;
  const ext = `.${getFileExtension(file.name)}`;
  return (
    ACCEPTED_MIME.includes(file.type.toLowerCase()) ||
    ACCEPTED_EXT.includes(ext)
  );
};

const isMp3File = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();
  return (
    getFileExtension(file.name) === "mp3" ||
    mimeType === "audio/mpeg" ||
    mimeType === "audio/mp3" ||
    mimeType === "audio/x-mpeg"
  );
};

export const getUnsupportedConversionReason = (
  files: File[],
  targetFormat: TargetMimeType,
): string | null => {
  if (targetFormat === "audio/ogg" && files.some(isMp3File)) {
    return "OGG output is not available for MP3 sources in this browser.";
  }

  return null;
};

export const isTargetFormatSupportedForFiles = (
  files: File[],
  targetFormat: TargetMimeType,
): boolean => getUnsupportedConversionReason(files, targetFormat) === null;

export const formatBytes = (bytes: number): string =>
  bytes > 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const convertAudio = (
  file: File,
  targetFormat: TargetMimeType,
  settings: ConversionSettings,
  onProgress?: (progress: number) => void,
): Promise<Blob> =>
  convertAudioWithFfmpeg(file, targetFormat, settings, onProgress);
